import React, { useEffect, useState } from 'react';
import Papa from 'papaparse';
import './AiTrustTerminal.css';

const GRADE_COLORS = {
  'AA': 'text-primary',
  'A': 'text-primary',
  'BBB': 'text-yellow-300',
  'BB': 'text-yellow-400',
  'B': 'text-red-400',
};

function getGradeColor(grade) {
  return GRADE_COLORS[grade] || 'text-green-200';
}

function getDeltaColor(val) {
  if (val > 0) return 'text-primary';
  if (val < 0) return 'text-red-400';
  return 'text-text-secondary';
}

function formatNumber(val, decimals = 0) {
  if (val === undefined || val === null) return '-';
  // Remove spaces and handle string numbers
  const num = typeof val === 'string' ? Number(val.trim()) : Number(val);
  if (isNaN(num)) return '-';
  return num.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function getNowString() {
  return new Date().toLocaleString();
}

function getScoreChangeTimeAgo() {
  return '1d ago';
}

const SCORE_CHANGE = '0.000';

export default function AiTrustRatingIndex() {
  const [data, setData] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState('');
  const [grade, setGrade] = useState('ALL');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [now, setNow] = useState(getNowString());

  useEffect(() => {
    const timer = setInterval(() => setNow(getNowString()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    fetch('/data/ai_maturity_graded.csv')
      .then((response) => {
        if (!response.ok) throw new Error('Failed to fetch CSV');
        return response.text();
      })
      .then((csvText) => {
        Papa.parse(csvText, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => {
            setData(results.data);
            setLoading(false);
          },
          error: (err) => {
            setError(err.message);
            setLoading(false);
          },
        });
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    let filtered = data;
    if (search) {
      const s = search.toLowerCase();
      filtered = filtered.filter(row =>
        (row.company || '').toLowerCase().includes(s) ||
        (row.sector || '').toLowerCase().includes(s)
      );
    }
    if (grade !== 'ALL') {
      filtered = filtered.filter(row => row.grade === grade);
    }
    setFiltered(filtered);
  }, [data, search, grade]);

  // Market summary
  const totalCompanies = data.length;
  const avgTrustScore = data.length ? Math.round(data.reduce((sum, r) => sum + Number(r.trustScore || 0), 0) / data.length) : 0;
  const topGrade = data.reduce((acc, r) => {
    if (!acc) return r.grade;
    const order = ['AA', 'A', 'BBB', 'BB', 'B'];
    return order.indexOf(r.grade) < order.indexOf(acc) ? r.grade : acc;
  }, null);
  const gradeDist = data.reduce((acc, r) => {
    acc[r.grade] = (acc[r.grade] || 0) + 1;
    return acc;
  }, {});
  const grades = ['AA', 'A', 'BBB', 'BB', 'B'];

  if (loading) return <div className="flex justify-center items-center h-screen bg-background text-primary">Loading...</div>;
  if (error) return <div className="flex justify-center items-center h-screen bg-background text-red-400">{error}</div>;

  return (
    <div className="min-h-screen bg-black text-primary font-mono flex flex-col uppercase">
      {/* Header */}
      <div className="flex items-center justify-between border-b-2 border-primary px-8 py-3 bg-black select-none">
        <div className="flex items-center gap-4">
          <span className="bg-black text-primary border border-primary px-3 py-1 font-bold tracking-widest text-xs">QFAI</span>
          <span className="text-2xl font-bold tracking-widest text-primary">AI MATURITY INDEX – FORTUNE100</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-primary/60 text-xs">{now}</span>
          <span className="bg-black text-primary border border-primary px-2 py-1 font-bold text-xs">LIVE</span>
        </div>
      </div>
      <div className="flex flex-1">
        {/* Main Table Area */}
        <div className="flex-1 flex flex-col p-4">
          {/* Search and Filter */}
          <div className="flex items-center gap-4 mb-3">
            <div className="relative w-96">
              <input
                type="text"
                placeholder="SEARCH: COMPANY/SECTOR..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="bg-black border-2 border-primary text-primary px-4 py-2 w-full font-mono uppercase tracking-widest focus:outline-none focus:ring-0 caret-primary terminal-caret"
                style={{ letterSpacing: '0.1em' }}
              />
              {/* Blinking green cursor (CSS) */}
              <span className="terminal-blink-cursor">&#9608;</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-primary/40 text-xs">GRADE:</span>
              <select
                value={grade}
                onChange={e => setGrade(e.target.value)}
                className="bg-black border-2 border-primary text-primary px-2 py-1 font-mono uppercase tracking-widest"
              >
                <option value="ALL">ALL</option>
                {grades.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
          </div>
          {/* Table */}
          <div className="overflow-x-auto border-2 border-primary bg-black">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="bg-black">
                  <th className="px-2 py-1 text-left text-primary border-b-2 border-primary text-xs tracking-widest">COMPANY</th>
                  <th className="px-2 py-1 text-left text-primary border-b-2 border-primary text-xs tracking-widest">SECTOR</th>
                  <th className="px-2 py-1 text-left text-primary border-b-2 border-primary text-xs tracking-widest">TRUST SCORE</th>
                  <th className="px-2 py-1 text-left text-primary border-b-2 border-primary text-xs tracking-widest">GRADE</th>
                  <th className="px-2 py-1 text-left text-primary border-b-2 border-primary text-xs tracking-widest">GOVERNANCE</th>
                  <th className="px-2 py-1 text-left text-primary border-b-2 border-primary text-xs tracking-widest">SECURITY</th>
                  <th className="px-2 py-1 text-left text-primary border-b-2 border-primary text-xs tracking-widest">PRIVACY</th>
                  <th className="px-2 py-1 text-left text-primary border-b-2 border-primary text-xs tracking-widest">ENABLEMENT</th>
                  <th className="px-2 py-1 text-left text-primary border-b-2 border-primary text-xs tracking-widest">LAST UPDATED</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row, i) => (
                  <tr key={i} className="hover:bg-primary/20 transition-colors">
                    <td className="px-2 py-1 border-b border-primary whitespace-nowrap max-w-xs truncate">{row.company}</td>
                    <td className="px-2 py-1 border-b border-primary whitespace-nowrap max-w-xs truncate">{row.sector}</td>
                    <td className="px-2 py-1 border-b border-primary whitespace-nowrap text-primary font-bold">{formatNumber(row.trustScore)}</td>
                    <td className={`px-2 py-1 border-b border-primary whitespace-nowrap font-bold ${getGradeColor(row.grade)}`}>{row.grade}</td>
                    {/* Governance */}
                    <td className="px-2 py-1 border-b border-primary whitespace-nowrap font-bold">
                      <span className={getDeltaColor(0)}>
                        {(() => {
                          const change = SCORE_CHANGE;
                          const time = getScoreChangeTimeAgo();
                          const score = formatNumber(row.governanceScore, 3);
                          return `${change} (${time}) [${score}]`;
                        })()}
                      </span>
                    </td>
                    {/* Security */}
                    <td className="px-2 py-1 border-b border-primary whitespace-nowrap font-bold">
                      <span className={getDeltaColor(0)}>
                        {(() => {
                          const change = SCORE_CHANGE;
                          const time = getScoreChangeTimeAgo();
                          const score = formatNumber(row.securityScore, 3);
                          return `${change} (${time}) [${score}]`;
                        })()}
                      </span>
                    </td>
                    {/* Privacy */}
                    <td className="px-2 py-1 border-b border-primary whitespace-nowrap font-bold">
                      <span className={getDeltaColor(0)}>
                        {(() => {
                          const change = SCORE_CHANGE;
                          const time = getScoreChangeTimeAgo();
                          const score = formatNumber(row.privacyScore, 3);
                          return `${change} (${time}) [${score}]`;
                        })()}
                      </span>
                    </td>
                    {/* Enablement */}
                    <td className="px-2 py-1 border-b border-primary whitespace-nowrap font-bold">
                      <span className={getDeltaColor(0)}>
                        {(() => {
                          const change = SCORE_CHANGE;
                          const time = getScoreChangeTimeAgo();
                          const score = formatNumber(row.enablementScore, 3);
                          return `${change} (${time}) [${score}]`;
                        })()}
                      </span>
                    </td>
                    <td className="px-2 py-1 border-b border-primary whitespace-nowrap text-primary/60">{getScoreChangeTimeAgo()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Footer */}
          <div className="w-full border-t-2 border-primary bg-black text-primary text-xs mt-2 px-1 py-2 flex items-center justify-between font-mono tracking-widest">
            <span>{filtered.length ? `1-${filtered.length} OF ${filtered.length} RECORDS` : '0 RECORDS'}</span>
            <span>1 SELECT COMPANY   2 SORT COLUMNS   ESC CLOSE DETAILS</span>
          </div>
        </div>
        {/* Sidebar */}
        <div className="w-80 border-l-2 border-primary p-4 bg-black flex flex-col gap-6">
          <div>
            <div className="text-primary font-bold text-lg mb-2">MARKET SUMMARY</div>
            <div className="flex flex-col gap-1 text-primary">
              <div>TOTAL COMPANIES: <span className="text-text font-bold">{totalCompanies}</span></div>
              <div>AVG TRUST SCORE: <span className="text-text font-bold">{avgTrustScore}</span></div>
              <div>TOP GRADE: <span className={`font-bold ${getGradeColor(topGrade)}`}>{topGrade}</span></div>
            </div>
          </div>
          <div>
            <div className="text-primary font-bold text-lg mb-2">GRADE DISTRIBUTION</div>
            <div className="flex flex-col gap-1">
              {grades.map(g => (
                <div key={g} className="flex items-center gap-2">
                  <span className={`w-8 font-bold ${getGradeColor(g)}`}>{g}:</span>
                  <span className="text-text">{gradeDist[g] || 0}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 